import { ITEMS, QUESTS, maxHp, type CharacterState, type SelfState } from '@sunderfen/shared';

/**
 * One-time hints for new players, shown at the moment they matter: a small card near the
 * quest tracker with "Got it" and "No more hints". Each hint shows once per character (kept
 * in this browser); players who know the game can turn them all off in Settings.
 */
type HintId = 'move' | 'quest' | 'gather' | 'full' | 'level' | 'cook' | 'smelt' | 'eat' | 'night' | 'grave' | 'map' | 'bank' | 'track' | 'knock';
const TEXT: Record<HintId, [string, string]> = {
  move: ['Getting around', 'Tap the ground to walk. Tap people and things to use them. The gold marker shows where your quest wants you.'],
  quest: ['A new quest', 'Your next step shows under your health. The Quests tab has every step, and tapping a quest there tracks it.'],
  gather: ['Into your pack', 'What you gather goes in your Pack. Press and hold (or right-click) anything for everything you can do with it.'],
  full: ['Your pack is full', 'Bank it in a lockbox or bank, drop things with a long-press, or carry a sack or basket: they fill before your pack does.'],
  level: ['Level up', 'Skills level as you use them. Open the Skills tab to see what each level opens up next.'],
  cook: ['Raw fish', 'Tap a fire or a range with raw fish in your pack to cook it. Cooked food heals; raw food makes you sick.'],
  smelt: ['Ore', 'Smelt ore into bars at a furnace, then hammer the bars into tools and gear at an anvil.'],
  eat: ['Low on health', 'Tap cooked food in your Pack to eat it, or walk away: most creatures give up the chase.'],
  night: ['Night falls', 'Shops shut and townsfolk go home. Their door has a lantern: tap it to knock and they will still talk to you.'],
  grave: ['You fell', 'You keep your three best things. The rest waits in a grave where you fell, for you alone for a while. Go back for it.'],
  map: ['The map', 'Press M or the map button to see the valley as far as you have explored it. Tap a place to walk there.'],
  bank: ['Banks', 'Every bank opens onto the same vault, wherever you are, so what you leave in one is waiting in all of them.'],
  track: ['More than one quest', 'You have a few quests on the go. Open the Quests tab and tap one to choose which the marker follows.'],
  knock: ['At home', 'They have gone in for the night. Tap the lantern at their door to knock.'],
};
const OFF_KEY = 'sunderfen-hints-off';

export class Hints {
  private seen = new Set<string>();
  private key = '';
  private queue: HintId[] = [];
  private showing: HintId | null = null;
  private el: HTMLDivElement;
  private timer = 0;
  private hadRaw = false; private hadOre = false; private hadItems = -1;
  off = false;

  constructor() {
    try { this.off = localStorage.getItem(OFF_KEY) === '1'; } catch { /* ignore */ }
    this.el = document.createElement('div');
    this.el.className = 'plate hint-card'; this.el.hidden = true; this.el.setAttribute('role', 'status');
    this.el.innerHTML = '<b></b><p></p><div class="hint-btns"><button type="button" class="btn small primary" data-k="ok">Got it</button><button type="button" class="btn small ghost" data-k="off">No more hints</button></div>';
    this.el.querySelector('[data-k="ok"]')!.addEventListener('click', () => this.next());
    this.el.querySelector('[data-k="off"]')!.addEventListener('click', () => { this.setOff(true); });
    document.getElementById('game')?.appendChild(this.el) ?? document.body.appendChild(this.el);
  }
  /** Who is playing: hints are remembered per character. */
  start(name: string) {
    this.key = `sunderfen-hints:${name.toLowerCase()}`;
    try { this.seen = new Set(JSON.parse(localStorage.getItem(this.key) ?? '[]')); } catch { this.seen = new Set(); }
  }
  setOff(off: boolean) {
    this.off = off; try { localStorage.setItem(OFF_KEY, off ? '1' : '0'); } catch { /* ignore */ }
    if (off) { this.queue = []; this.hide(); }
    const t = document.getElementById('hints-toggle'); if (t) { t.setAttribute('aria-pressed', String(!off)); t.textContent = off ? 'Hints off' : 'Hints on'; }
  }
  show(id: HintId, delayMs = 0) {
    if (this.off || this.seen.has(id) || this.queue.includes(id) || this.showing === id) return;
    if (delayMs) { setTimeout(() => this.show(id), delayMs); return; }
    this.queue.push(id);
    if (!this.showing) this.next();
  }
  private next() {
    if (this.showing) { this.seen.add(this.showing); try { localStorage.setItem(this.key, JSON.stringify([...this.seen])); } catch { /* ignore */ } }
    this.showing = null; clearTimeout(this.timer);
    const id = this.queue.shift();
    if (!id || this.off) { this.hide(); return; }
    this.showing = id;
    const [t, p] = TEXT[id];
    this.el.querySelector('b')!.textContent = t; this.el.querySelector('p')!.textContent = p;
    this.el.hidden = false; this.el.classList.remove('in'); void this.el.offsetWidth; this.el.classList.add('in');
    // read it, then it gets out of the way by itself (it still counts as seen)
    this.timer = window.setTimeout(() => this.next(), 22000);
  }
  private hide() { this.el.hidden = true; }

  /* ---------------- what sets them off ---------------- */
  /** Look at the character after each change. */
  watch(s: SelfState) {
    const items = s.inv.filter(Boolean).length;
    if (this.hadItems >= 0 && items > this.hadItems) this.show('gather');
    this.hadItems = items;
    if (s.inv.length && s.inv.every(Boolean)) this.show('full');
    const raw = s.inv.some(id => !!id && (ITEMS[id]?.cat === 'raw' || ITEMS[id]?.cat === 'meat'));
    if (raw && !this.hadRaw) this.show('cook', 1200);
    this.hadRaw = raw;
    const ore = s.inv.some(id => !!id && ITEMS[id]?.cat === 'ore');
    if (ore && !this.hadOre) this.show('smelt', 1200);
    this.hadOre = ore;
    const max = maxHp({ ...s, revealCarry: 0 } as CharacterState);
    if (s.hp > 0 && s.hp < max * 0.4) this.show('eat');
    const active = Object.keys(s.quests).filter(k => QUESTS[k] && !s.quests[k].done);
    if (active.length >= 3) this.show('track', 2500);
    if (s.flags.includes('ferried')) this.show('map', 6000);
  }
  /** A fresh arrival: how to move, straight away. */
  arrived(s: SelfState) { if (!s.flags.includes('ferried')) this.show('move', 1500); }
  quest(state: 'start' | 'step' | 'done', id: string) { if (state === 'start' && id !== 'gull_rock') this.show('quest', 800); }
  levelUp() { this.show('level', 600); }
  died() { this.show('grave', 2500); }
  night() { this.show('night', 1000); }
  banked() { this.show('bank'); }
  knock() { this.show('knock'); }
}
